import axios from "axios";

export const FILE_BASE_URL = (process.env.REACT_APP_FILE_URL || "http://localhost:3012").replace(
  /\/+$/,
  ""
);

const API_BASE_URL = (process.env.REACT_APP_APIURL || "http://localhost:3012/api").replace(
  /\/+$/,
  ""
);

const isProbablyRawBase64 = (value) =>
  value.length > 100 && !value.includes("/") && !value.includes("\\") && !value.includes(".");

/**
 * Resolve a stored file path or legacy base64 value to a browser-loadable URL.
 */
export function getFileUrl(storedValue, { fileType = "image" } = {}) {
  if (storedValue == null || storedValue === "") {
    return "";
  }

  const value = String(storedValue).trim();
  if (!value) {
    return "";
  }

  if (value.startsWith("data:")) {
    return value;
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  if (value.startsWith("/getfiles/")) {
    return `${FILE_BASE_URL}${value}`;
  }

  if (value.startsWith("getfiles/")) {
    return `${FILE_BASE_URL}/${value}`;
  }

  const fileuploadsIdx = value.indexOf("/fileuploads/");
  if (fileuploadsIdx !== -1) {
    return `${FILE_BASE_URL}/getfiles${value.slice(fileuploadsIdx + "/fileuploads".length)}`;
  }

  if (isProbablyRawBase64(value)) {
    const mime = fileType === "pdf" ? "application/pdf" : "image/jpeg";
    return `data:${mime};base64,${value}`;
  }

  if (!value.includes("/")) {
    return `${FILE_BASE_URL}/getfiles/uploads/${value}`;
  }

  return `${FILE_BASE_URL}${value.startsWith("/") ? "" : "/"}${value}`;
}

/**
 * Upload a file via /uploadImage and return the stored path for DB persistence.
 */
export async function uploadFile(file, fieldName = "image") {
  if (!file) {
    throw new Error("No file provided");
  }

  const formData = new FormData();
  formData.append(fieldName, file);

  const response = await axios.post(`${API_BASE_URL}/uploadImage`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  if (!response.data?.status || !response.data?.data?.path) {
    throw new Error(response.data?.message || "File upload failed");
  }

  return response.data.data.path;
}

/**
 * Resolve a merchant logo record (or stored path) to a browser-loadable URL.
 */
export function getMerchantLogoUrl(logo) {
  if (logo == null || logo === "") {
    return "";
  }

  if (typeof logo === "string") {
    return getFileUrl(logo);
  }

  return getFileUrl(
    logo.publicUrl ||
      logo.publicPath ||
      logo.path ||
      logo.filename ||
      ""
  );
}

/**
 * Prefer the user-facing original name over the stored disk filename.
 */
export function getDisplayFileName(document, fallback = "Document") {
  if (!document) return fallback;

  if (typeof document === "string") {
    const parts = document.split(/[/\\]/);
    return parts[parts.length - 1] || document;
  }

  if (document.file_name) return document.file_name;
  if (document.originalName) return document.originalName;
  if (document.originalname) return document.originalname;

  const storedPath = document.file || document.path || document.filename;
  if (storedPath) {
    const parts = String(storedPath).split(/[/\\]/);
    return parts[parts.length - 1] || storedPath;
  }

  return fallback;
}

export function openStoredFile(storedValue, fileType = "image") {
  const url = getFileUrl(storedValue, { fileType });
  if (!url) {
    return;
  }

  if (fileType === "pdf" && url.startsWith("data:")) {
    const base64 = url.includes(",") ? url.split(",")[1] : storedValue;
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i += 1) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });
    window.open(URL.createObjectURL(blob), "_blank");
    return;
  }

  if (fileType === "image" && url.startsWith("data:")) {
    const win = window.open();
    if (win) {
      win.document.write("<html><head><title>Image</title></head><body style='margin:0'>");
      win.document.write(
        `<img src="${url}" style="max-width:100vw;max-height:100vh;display:block;margin:auto;" />`
      );
      win.document.write("</body></html>");
      win.document.close();
    }
    return;
  }

  window.open(url, "_blank");
}

export function downloadStoredFile(storedValue, downloadName, fileType = "image") {
  const url = getFileUrl(storedValue, { fileType });
  if (!url) {
    return;
  }

  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = downloadName;
  anchor.target = "_blank";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}
